import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { ArrowRight, Building2, MapPin, Target, TrendingUp, Users, CheckCircle2, Quote, Play, Star, X, Rocket, PieChart, Lightbulb, Trophy } from "lucide-react";
import CompanyLayout from "@/components/CompanyLayout";
import { DemoBookingModal } from "@/components/DemoBookingModal";
import { SupademoModal } from "@/components/SupademoModal";
import companyLogo from "@/assets/case-studies/beon-ti-ntg-logo.png";
import nataliePhoto from "@/assets/case-studies/natalie-cook.jpeg";
import happyTeamImage from "@/assets/case-studies/happy-employees-team.jpg";
import { usePostHog } from "@/hooks/usePostHog";

// Author info (used throughout testimonials)
const AUTHOR = {
  name: "Natalie Cook",
  title: "VP of Employee Experience",
  company: "TI/NTG",
  photo: nataliePhoto
};

// Reusable Testimonial Component
interface TestimonialProps {
  quote: string;
  variant?: 'blue' | 'purple' | 'white';
  size?: 'default' | 'large';
  showAuthor?: boolean;
}

const Testimonial: React.FC<TestimonialProps> = ({ 
  quote, 
  variant = 'blue', 
  size = 'default',
  showAuthor = false 
}) => {
  const variantStyles = {
    blue: "bg-blue-50 border-l-4 border-blue-600",
    purple: "bg-blue-50 border-l-4 border-blue-600",
    white: "bg-gray-50 border-l-4 border-gray-300"
  };

  const quoteColor = 'text-blue-600';
  const textSize = size === 'large' ? 'text-xl' : 'text-lg';

  return (
    <div className={`${variantStyles[variant]} p-6 my-8 rounded-lg`}>
      <Quote className={`h-8 w-8 ${quoteColor} mb-4`} />
      <blockquote className={`${textSize} text-gray-800 italic ${showAuthor ? 'mb-4' : ''}`}>
        {quote}
      </blockquote>
      {showAuthor && (
        <div className="flex items-center gap-4 mt-6">
          <img 
            src={AUTHOR.photo} 
            alt={AUTHOR.name} 
            className={`${size === 'large' ? 'w-14 h-14' : 'w-12 h-12'} rounded-full object-cover`}
          />
          <div>
            <p className={`${size === 'large' ? 'font-bold' : 'font-semibold'} text-gray-900`}>
              {AUTHOR.name}
            </p>
            <p className="text-sm text-gray-600">
              {AUTHOR.title}{size === 'large' ? `, ${AUTHOR.company}` : ''}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

const TINTGCaseStudy = () => {
  const [isDemoModalOpen, setIsDemoModalOpen] = useState(false);
  const [isSupademoModalOpen, setIsSupademoModalOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('background');
  const { posthog } = usePostHog();

  // Track active section on scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { threshold: 0.3, rootMargin: '-100px 0px -60% 0px' }
    );

    const sections = document.querySelectorAll('[data-section]');
    sections.forEach((section) => observer.observe(section));

    return () => observer.disconnect();
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 100;
      const top = element.getBoundingClientRect().top + window.pageYOffset - offset;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  const sections = [
    { id: 'background', label: 'Background' },
    { id: 'journey', label: 'The Journey' },
    { id: 'implementation', label: 'Implementation' },
    { id: 'results', label: 'Results' },
    { id: 'customer-experience', label: 'Customer Experience' },
    { id: 'success-story', label: 'Success Story' },
    { id: 'outcome', label: 'Outcome' },
    { id: 'takeaways', label: 'Key Takeaways' }
  ];

  const painPoints = [
    'Inconsistent Recognition',
    'Low Visibility',
    'No Structure',
    'Limited Engagement'
  ];

  return (
    <>
      <DemoBookingModal 
        open={isDemoModalOpen} 
        onOpenChange={setIsDemoModalOpen} 
      />
      <SupademoModal 
        open={isSupademoModalOpen} 
        onOpenChange={setIsSupademoModalOpen} 
      />
      <CompanyLayout
      title="TI/NTG Case Study: Building Employee Recognition Culture"
      description="Discover how TI/NTG achieved 85% monthly employee recognition and increased engagement scores by 15% using Assembly's recognition platform."
    >
      <div className="bg-white">
        {/* Hero Section */}
        <section className="relative bg-gray-50 pt-24 pb-16 border-b border-gray-200">
          <div className="container mx-auto px-4 2xl:px-12 max-w-none lg:max-w-7xl xl:max-w-[1400px] 2xl:max-w-[1600px]">
            <div className="flex flex-col lg:grid lg:grid-cols-7 gap-8 lg:gap-12 items-center">
              <div className="lg:col-span-4 flex flex-col justify-center">
                <div className="flex items-center gap-4 mb-6">
                  <div className="inline-flex items-center bg-white px-4 py-2 rounded-full border border-gray-200">
                    <span className="text-sm font-medium text-gray-600">Case Study</span>
                  </div>
                  <img 
                    src={companyLogo} 
                    alt="Beon TI NTG" 
                    className="h-6 object-contain"
                  />
                </div>
                <h1 className="text-3xl sm:text-4xl lg:text-4xl xl:text-5xl font-bold text-gray-900 mb-6 leading-tight">
                  How TI/NTG Built a Culture of Employee Recognition and Connection with Assembly
                </h1>
                <p className="text-lg sm:text-xl text-gray-600 mb-8 leading-relaxed">
                  Transforming employee recognition from ad-hoc gestures to a structured, company-wide culture of appreciation and connection.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 mb-6">
                  <Button 
                    size="lg" 
                    className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white" 
                    onClick={() => setIsDemoModalOpen(true)}
                  >
                    Book a Demo
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                  <Button 
                    variant="outline" 
                    size="lg" 
                    className="w-full sm:w-auto border-gray-300 text-gray-700 hover:bg-gray-50 hover:text-gray-900 hover:border-gray-400" 
                    onClick={() => {
                      posthog?.capture('see_it_in_action_clicked', {
                        page: '/resources/case-studies/ti-ntg',
                        section: 'hero',
                        button_text: 'See it in Action',
                        url: window.location.href,
                        opens_modal: true
                      });
                      setIsSupademoModalOpen(true);
                    }}
                  >
                    See it in Action
                    <Play className="w-4 h-4 ml-2" fill="currentColor" />
                  </Button>
                </div>
                
                {/* G2 Rating Badge */}
                <div className="flex flex-col sm:flex-row gap-3 sm:gap-6 items-start sm:items-center text-sm text-gray-600">
                  <div className="flex items-center gap-2">
                    <Star className="h-4 w-4 text-yellow-400 fill-yellow-400" />
                    <span className="font-medium">4.9 / 5 on G2</span>
                    <img src="/lovable-uploads/e68d106f-69a9-45e8-8624-7f09b1ae1e6f.png" alt="G2 Logo" className="h-4 w-4" />
                  </div>
                  <a href="https://www.g2.com/products/assembly-2021-12-09/reviews" target="_blank" rel="noopener noreferrer" className="hover:text-blue-600 transition-colors">
                    3000+ reviews
                  </a>
                </div>
              </div>
              <div className="lg:col-span-3 relative w-full">
                <div className="aspect-[4/3] rounded-xl overflow-hidden border border-gray-200">
                  <img 
                    src={happyTeamImage} 
                    alt="Happy team collaboration" 
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Main Content with Sidebar Layout */}
        <div className="bg-white">
          <div className="container mx-auto px-4 2xl:px-12 max-w-none lg:max-w-7xl xl:max-w-[1400px] 2xl:max-w-[1600px]">
            <div className="flex flex-col lg:flex-row gap-8 py-12">
              
              {/* Sticky Sidebar */}
              <aside className="lg:w-80 flex-shrink-0 mx-auto lg:mx-0 max-w-sm lg:max-w-none">
                <div className="lg:sticky lg:top-24">
                  <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                    
                    {/* Reviewer Section */}
                    <div className="p-6 text-center lg:text-left">
                      <img 
                        src={nataliePhoto} 
                        alt={AUTHOR.name} 
                        className="w-20 h-20 rounded-full object-cover mb-4 border-4 border-blue-100 mx-auto lg:mx-0"
                      />
                      <h3 className="font-bold text-gray-900 text-lg mb-1">{AUTHOR.name}</h3>
                      <p className="text-sm text-gray-600 mb-4">{AUTHOR.title}</p>
                      <img 
                        src={companyLogo} 
                        alt="TI/NTG" 
                        className="h-8 object-contain mx-auto lg:mx-0"
                      />
                    </div>

                    {/* Company Details */}
                    <div className="px-6 py-4 border-t border-gray-100 space-y-4 text-center lg:text-left">
                      <div>
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Industry</p>
                        <p className="text-sm font-medium text-gray-900">Logistics & Transportation</p>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Company Size</p>
                        <p className="text-sm font-medium text-gray-900">Enterprise</p>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Location</p>
                        <p className="text-sm font-medium text-gray-900">Atlanta, Georgia</p>
                      </div>
                    </div>

                    {/* Section Navigation - Hidden on mobile/tablet */}
                    <nav className="hidden lg:block px-6 py-4 border-t border-gray-100">
                      <h3 className="font-bold text-gray-900 mb-4 text-sm uppercase tracking-wide">Jump To</h3>
                      <ul className="space-y-2">
                        {sections.map((section) => (
                          <li key={section.id}>
                            <button
                              onClick={() => scrollToSection(section.id)}
                              className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-all ${
                                activeSection === section.id
                                  ? 'bg-blue-100 text-blue-900 font-semibold'
                                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                              }`}
                            >
                              {section.label}
                            </button>
                          </li>
                        ))}
                      </ul>
                    </nav>
                  </div>
                </div>
              </aside>

              {/* Main Content */}
              <main className="flex-1 min-w-0">
                {/* Results Highlight Card */}
                <div className="bg-blue-600 rounded-xl p-8 mb-12 text-white border border-blue-700">
                  <div className="grid md:grid-cols-2 gap-8">
                    <div>
                      <div className="text-5xl font-bold mb-2">85%</div>
                      <p className="text-blue-100">Monthly Employee Recognition</p>
                    </div>
                    <div>
                      <div className="text-5xl font-bold mb-2">15%</div>
                      <p className="text-blue-100">Increase in Recognition Scores</p>
                    </div>
                  </div>
                </div>

        {/* Background Section */}
        <section id="background" data-section className="mb-16">
          <div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Background: A Growing Company with a Recognition Gap
            </h2>
            <div className="prose prose-lg max-w-none">
              <p className="text-gray-700 leading-relaxed mb-6">
                When Natalie Cook, Vice President of Employee Experience at TI (Transportation Insight) and NTG (Nolan Transportation Group) took stock of the company culture, one insight stood out: recognition was happening—but not equally and not visibly.
              </p>
              
              <Testimonial 
                quote="Recognition wasn't consistent across teams. Some celebrated milestones like birthdays and anniversaries, others didn't. We wanted to make sure every employee felt seen, appreciated, and valued, no matter where they were."
              />

              <p className="text-gray-700 leading-relaxed">
                Before adopting Assembly, appreciation at TI/NTG often came through emails, Slack messages, or small in-office gestures. Though heartfelt, these ad-hoc efforts lacked structure and cross-team visibility. The leadership team realized that recognition needed to be woven into the company's daily workflow and not treated as an afterthought. This was essential not just for morale but for promoting diversity and inclusion in the workplace, where every contribution could be celebrated.
              </p>
            </div>
          </div>
        </section>

        {/* Journey Section */}
        <section id="journey" data-section className="mb-16">
          <div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              The Journey to the Right Employee Recognition Platform
            </h2>
            <div className="prose prose-lg max-w-none">
              <p className="text-gray-700 leading-relaxed mb-6">
                Cook and her Employee Experience team began exploring platforms that could bring consistency, visibility, and delight to the employee recognition process, and double up as a foundation for team building activities and staff appreciation gifts. A simple Google search led them to Assembly - and the first impression was promising.
              </p>

              <Testimonial 
                quote="The high level of customization stood out immediately. We could set our own point names, customize allowances, and access a wide range of rewards - all at a really competitive price."
                variant="white"
              />

              <p className="text-gray-700 leading-relaxed">
                During the evaluation phase, the AI-powered reporting caught their attention. The ability to ask questions and instantly pull insights made it easy for leaders to track engagement trends and participation. The variety of reward options, from company-branded swag to digital gift cards and custom awards, offered inexpensive employee engagement ideas that fit perfectly with their vision of companies with great culture.
              </p>
            </div>
          </div>
        </section>

        {/* Implementation Section */}
        <section id="implementation" data-section className="mb-16">
          <div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Implementation: Instant Adoption, Zero Friction
            </h2>
            <div className="prose prose-lg max-w-none">
              <p className="text-gray-700 leading-relaxed mb-6">
                Rolling out new software across an organization can be challenging — but Assembly proved to be the exception.
              </p>

              <Testimonial 
                quote="Our team adopted it immediately. It's so intuitive that we didn't even need formal training."
                showAuthor={false}
              />

              <p className="text-gray-700 leading-relaxed mb-6">
                Within days, employees were giving daily shout-outs, celebrating milestones, and participating in team building activities through the platform. Recognition quickly became part of the company's rhythm. So much so that Natalie Cook jokes,
              </p>

              <p className="text-gray-700 leading-relaxed italic">
                "The volume of recognition is so high, it's actually hard to keep up — which is a great problem to have!"
              </p>
            </div>
          </div>
        </section>

        {/* Results Section */}
        <section id="results" data-section className="mb-16">
          <div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Results: Building a Culture That Recognizes Every Win
            </h2>
            
            <div className="prose prose-lg max-w-none">
              <p className="text-gray-700 leading-relaxed mb-6">
                The impact was measurable — and meaningful. Within months, 85% of the workforce was receiving recognition every month. Engagement survey scores improved significantly, with a 15% jump in the statement "I have received recognition or praise for doing good work."
              </p>

              <p className="text-gray-700 leading-relaxed mb-6">
                Cross-functional appreciation also saw a spike. Departments that rarely interacted began connecting through shout-outs, sparking collaboration and mutual respect.
              </p>

              <Testimonial 
                quote="Overall engagement is the highest it's ever been. People love giving shout-outs. It's helped us build stronger relationships across teams and geographies."
                variant="white"
              />

              <p className="text-gray-700 leading-relaxed">
                By creating a space for genuine connection and reward, TI/NTG positioned itself among companies with great culture, championing inclusivity and continuous encouragement.
              </p>
            </div>
          </div>
        </section>

        {/* Customer Experience Section */}
        <section id="customer-experience" data-section className="mb-16">
          <div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Customer Experience: Support That Feels Effortless
            </h2>
            <div className="prose prose-lg max-w-none">
              <p className="text-gray-700 leading-relaxed mb-6">
                The TI/NTG team describes Assembly's customer support as "low-touch but high-value." The platform's simplicity means they rarely need help, but when they do, a quick email or call solves it instantly.
              </p>

              <p className="text-gray-700 leading-relaxed mb-6">
                Even the onboarding process, typically a pain point in tech rollouts, was smooth. Employees required no formal training, and most feedback centered on how easy and enjoyable the tool was to use.
              </p>

              <div className="bg-blue-50 p-6 rounded-lg my-8">
                <p className="text-lg text-gray-800 font-medium italic">
                  "It's low-touch but high-value — when we do need help, it's instant."
                </p>
              </div>

              <p className="text-gray-700 leading-relaxed">
                The only feature wish? "More flexibility in customizing colors and branding — but that's just us wanting to make it feel even more like TI/NTG."
              </p>
            </div>
          </div>
        </section>

        {/* Success Story Section */}
        <section id="success-story" data-section className="mb-16">
          <div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Success Story: Turning Steps into Smiles
            </h2>
            
            <div className="prose prose-lg max-w-none">
              <p className="text-gray-700 leading-relaxed mb-6">
                One of the most memorable moments for the team was the "300K Steps Challenge" held in April. The initiative encouraged employees to stay active, and the response was overwhelming.
              </p>

              <Testimonial 
                quote="It got people taking lunchtime walks together and sharing updates on the platform. It wasn't just about fitness - it was about connection and fun."
                variant="white"
                showAuthor={false}
              />

              <p className="text-gray-700 leading-relaxed">
                This single event became a cultural turning point, illustrating how recognition tools can bring teams together around shared goals beyond work tasks. It also showcased how inexpensive employee engagement ideas can have an enormous impact on morale.
              </p>
            </div>
          </div>
        </section>

        {/* Outcome Section */}
        <section id="outcome" data-section className="mb-16">
          <div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Outcome: A More Connected, Engaged, and Appreciated Workforce
            </h2>
            <div className="prose prose-lg max-w-none">
              <p className="text-gray-700 leading-relaxed mb-6">
                Assembly has now become an integral part of TI/NTG's employee experience strategy. It has helped them strengthen engagement, build inter-departmental trust, and make recognition part of everyday life - not just performance reviews.
              </p>

              <p className="text-gray-700 leading-relaxed mb-8 italic">
                "Our employees often ask, 'Why didn't we have this sooner?' That's when you know it's making a difference," smiles Cook.
              </p>

              <p className="text-gray-700 leading-relaxed mb-6">
                When asked to describe Assembly in one line, Natalie Cook puts it perfectly:
              </p>

              <Testimonial 
                quote="Assembly is a fun, easy recognition platform that empowers employees to give kudos and shout-outs, helping us build a more connected, engaged, and happy workplace."
                variant="blue"
                size="large"
              />
            </div>
          </div>
        </section>

        {/* Key Takeaways Section */}
        <section id="takeaways" data-section className="mb-16">
          <div className="bg-gray-50 p-8 md:p-12 rounded-xl border border-gray-200">
            <div className="mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Key Takeaways
              </h2>
              <p className="text-lg text-gray-600">
                Measurable results that transformed employee recognition at TI/NTG
              </p>
            </div>
            
            {/* Featured Stats */}
            <div className="grid md:grid-cols-2 gap-8 mb-12 pb-12 border-b border-gray-200">
              <div>
                <div className="text-5xl font-bold text-gray-900 mb-2">85%</div>
                <p className="text-gray-600 text-lg">of employees receive recognition each month</p>
              </div>
              <div>
                <div className="text-5xl font-bold text-gray-900 mb-2">15%</div>
                <p className="text-gray-600 text-lg">improvement in recognition-related engagement scores</p>
              </div>
            </div>

            {/* Other Takeaways */}
            <div className="grid md:grid-cols-2 gap-x-12 gap-y-6">
              {[
                "Seamless onboarding - adopted organization-wide within days",
                "AI-powered analytics enabling leadership to measure engagement",
                "Increased cross-team collaboration and employee morale",
                "Promotes diversity and inclusion in the workplace",
                "Encourages inexpensive employee engagement ideas",
                "Inspires team building activities and appreciation culture"
              ].map((takeaway, index) => (
                <div key={index} className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <p className="text-gray-700">{takeaway}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* About NTG Section */}
        <section className="mb-16">
          <div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              About NTG (Nolan Transportation Group)
            </h2>
            <div className="bg-gray-50 p-8 rounded-xl border border-gray-200">
              <p className="text-gray-700 leading-relaxed text-lg">
                NTG is a leading third-party logistics and freight brokerage company providing truckload and less-than-truckload services. Together with Transportation Insight (TI), NTG leverages its Beon digital logistics platform to optimize shipping operations and carrier management. The company serves thousands of shippers, driven by its people-first culture and commitment to operational excellence.
              </p>
            </div>
          </div>
        </section>
              </main>
            </div>
          </div>
        </div>

        {/* CTA Footer Section */}
        <section className="py-20 bg-blue-600 border-t border-blue-700">
          <div className="container mx-auto px-4 max-w-5xl text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Smooth starts lead to stronger partnerships - ready to begin yours?
            </h2>
            <div className="mt-10">
              <Button 
                size="lg" 
                className="bg-white text-blue-600 hover:bg-gray-100"
                onClick={() => setIsDemoModalOpen(true)}
              >
                Book a Demo
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        </section>
      </div>
      </CompanyLayout>
    </>
  );
};

export default TINTGCaseStudy;
